import { useEffect, useState } from "react";
import { Brain } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { endpoints } from "../lib/api";

const sideColor = (d) =>
  d === "LONG" ? "text-signal-buy" : d === "SHORT" ? "text-signal-sell" : "text-muted-foreground";

export const NeurobroPanel = ({ symbol }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!symbol) return;
    setLoading(true);
    try {
      const { data } = await endpoints.neurobro(symbol);
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
  }, [symbol]);

  const side = data?.side || (data && !data.error ? "NEUTRAL" : null);

  return (
    <div
      data-testid="neurobro-panel"
      className="border border-border rounded-sm bg-[#0d0d0d] relative overflow-hidden"
      style={{
        backgroundImage:
          "linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(0,0,0,0) 60%)",
      }}
    >
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={16} weight="fill" className="text-signal-buy" />
          <div>
            <div className="font-heading font-bold text-sm">AI Signal</div>
            <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
              Sumber sinyal entry bot
            </div>
          </div>
        </div>
        <Button
          data-testid="neurobro-run-btn"
          onClick={run}
          disabled={loading || !symbol}
          size="sm"
          variant="outline"
          className="rounded-sm h-8"
        >
          {loading ? "Analyzing..." : "Analyze"}
        </Button>
      </div>

      {!data && !loading && (
        <div className="p-4 text-sm text-muted-foreground font-mono">
          Klik &quot;Analyze&quot; untuk analisa AI terhadap <span className="text-white">{symbol}</span>.
        </div>
      )}

      {loading && (
        <div className="p-4 text-sm text-muted-foreground font-mono flex items-center">
          <span className="dot-live inline-block mr-2" /> Menghubungi AI...
        </div>
      )}

      {data && !loading && (
        <div className="p-4 space-y-4">
          {data.error ? (
            <div className="text-sm font-mono text-signal-sell" data-testid="neurobro-error">
              Error: {data.error === "missing_api_key" ? "API key belum diisi (Settings)" : data.error}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between border border-border rounded-sm p-3 bg-[#0a0a0a]">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Rekomendasi
                  </div>
                  <div
                    className={`font-heading font-black text-3xl tracking-tight ${sideColor(side)}`}
                    data-testid="neurobro-side"
                  >
                    {side}
                  </div>
                </div>
                <div className="text-right font-mono text-[11px] space-y-1">
                  <div>
                    Confidence:{" "}
                    <span className="text-white font-bold">{data.confidence ?? 0}%</span>
                  </div>
                  <div>
                    Risk:{" "}
                    <span className="text-white uppercase">{data.risk || "-"}</span>
                    {data.cached && <span className="text-muted-foreground ml-1">· cached</span>}
                  </div>
                  {data.source && (
                    <div>
                      Sumber:{" "}
                      <span className={data.source === "claude" ? "text-signal-brand uppercase" : "text-signal-buy uppercase"} data-testid="signal-source-label">
                        {data.source}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="font-mono text-[11px] text-muted-foreground">
                Sentiment: <span className={sideColor(side)}>{data.sentiment}</span> · Harga saat analisa: $
                {Number(data.price).toLocaleString("en-US", { maximumFractionDigits: 6 })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NeurobroPanel;
